import * as Sentry from "@sentry/vue";
import { useAuthenticationHandshake } from "@/composable/useAuthenticationHandshake";
import { useAuthenticationStore } from "@/stores/authenticationStore";
import { useGlobalLoadingScreen } from "@/stores/globalLoadingScreenStore";
import { useValidateToken } from "@/composable/useValidateToken";
import { useAuthUserStore } from "@/stores/authUserStore";
import { useRoutePermission } from "@/composable/useRoutePermission";
import { useIsEmbeddedStore } from "@/stores/isEmbeddedStore";
import {
    usePredefinedDocumentStore
} from "@/stores/predefinedDocuments/usePredefinedDocumentStore.ts";
import {
    useCheckoutCartManagement
} from "@/stores/checkout/composable/useCheckoutCartManagement.ts";
import { useAppVersionCheck } from "@/composable/useAppVersionCheck";
import { useAppVersionStore } from "@/stores/appVersionStore";

export const useAppBootstrap = () => {
    const authentication = useAuthenticationStore();
    const appVersionStore = useAppVersionStore();
    const handshake = useAuthenticationHandshake();
    const globalLoading = useGlobalLoadingScreen();
    const validateToken = useValidateToken();
    const authUser = useAuthUserStore();
    const routePermission = useRoutePermission();
    const isEmbeddedStore = useIsEmbeddedStore();
    const cartManagement = useCheckoutCartManagement();
    const predefinedDocumentsStore = usePredefinedDocumentStore();
    const appVersionCheck = useAppVersionCheck();

    const initialize = async () => {
        authentication.retrieveAccessTokenFromLocalStorage();
        await appVersionCheck.initialize();

        globalLoading.activateLoadingScreen(async () => {
            isEmbeddedStore.initialize();

            await handshake.initializeToken();

            await validateToken.initialize();

            // An invalid token already triggered authenticationStore.logout(), which strips
            // the stored token and redirects to login; stop here so the calls below don't
            // fire with no token and 401 in the window before the redirect takes effect.
            if (!validateToken.tokenIsValid.value) return;

            await authUser.fetch();

            Sentry.setUser({
                id: authUser.authUser?.id?.toString(),
                username: authUser.authUser?.username,
            });
            Sentry.setTag("mainRole", authUser.mainRole);
            Sentry.setTag("businessUnit", authUser.mainBusinessUnit?.name);

            await routePermission.initialize();

            try {
                await cartManagement.getOrCreateCart();
            } catch {
                // Already reported to Sentry inside getOrCreateCart(); a failed cart fetch
                // shouldn't block the rest of app init.
            }

            globalLoading.deactivateLoadingScreen();

            await predefinedDocumentsStore.getPredefinedDocuments();
        });
    };

    const dispose = () => {
        validateToken.removeTokenIntervalChecker();
        appVersionCheck.removeVersionIntervalChecker();
        appVersionStore.dispose();
        authentication.dispose();
    };

    return {
        initialize,
        dispose
    };
};
